import * as React from "react";
import type { FlowDefinition, NodeDefinition, TurnBudgetDefinition } from "./types";
import type { NodeRunMeta } from "./FlowRunView";
import { MAX_TRACE_ENTRY_CHARS, type NodeTraceEntry } from "./runTrace";
import { MAX_ACTION_DETAIL_CHARS, type ActionRecord } from "./actionLog";
import { fmtBytes } from "./RunGraphs";

/**
 * The debugger surface of the run view (flow-debugger-ui W2).
 *
 * Rendering already existed — Visualizer v2 ships the graph, pills, live bubble and RunLog. What
 * this module adds is the part that makes it a *debugger* rather than a viewer: a toolbar whose
 * verbs are legal or visibly disabled, breakpoints, and the payload of the node the run is held at,
 * so releasing a step is an informed decision instead of a reflex.
 *
 * Everything here is presentational. Legality is NOT computed locally — it arrives in
 * `RunDebugState.legal` from the host's FlowDebugController, because two implementations of "which
 * verbs are legal now" would eventually disagree and the panel would offer a button that does
 * nothing.
 */

/** Control verbs. Mirrors `DebugVerb` in the extension's flows/FlowDebugController.ts. */
export type DebugVerb =
    | "continue"
    | "step"
    | "run-to"
    | "restart-node"
    | "abort"
    | "pause";

/** Run-control state pushed by the host. Mirrors `DebugState`. */
export interface RunDebugState {
    instanceId: string;
    status: "running" | "paused" | "awaiting-approval" | "done";
    atNodeId?: string;
    reason?: string;
    mode: "run" | "step" | "run-to";
    runToTarget?: string;
    breakpoints: string[];
    /** Authoritative — the toolbar enables exactly these. */
    legal: DebugVerb[];
    finished: boolean;
}

/** A `requireApproval` decision the panel is hosting. Mirrors `FlowApprovalRequest`. */
export interface RunApprovalRequest {
    instanceId: string;
    nodeId: string;
    nodeName: string;
    model?: string;
    prompt: string;
    outcomes?: Array<{ condition: string; targetLabel: string; unconditional: boolean }>;
    denialTargetLabel?: string | null;
    prevStep?: { nodeId: string; durationMs?: number; resultTail: string };
}

const VERB_SPEC: Array<{
    verb: DebugVerb;
    glyph: string;
    label: string;
    hint: string;
}> = [
    { verb: "continue", glyph: "▶", label: "Continue", hint: "Run until a breakpoint, an approval gate or the end (F5)" },
    { verb: "step", glyph: "⤼", label: "Step", hint: "Run exactly one node, then stop (F10)" },
    { verb: "run-to", glyph: "⏭", label: "Run to", hint: "Run until the selected node, stopping before it executes" },
    { verb: "restart-node", glyph: "↻", label: "Restart node", hint: "Re-run the node the run is held at, with the same input" },
    { verb: "pause", glyph: "⏸", label: "Pause", hint: "Stop at the next node boundary (never mid-node)" },
    { verb: "abort", glyph: "⏹", label: "Abort", hint: "Cancel the run" },
];

const STATUS_TEXT: Record<RunDebugState["status"], string> = {
    running: "running",
    paused: "paused",
    "awaiting-approval": "awaiting approval",
    done: "finished",
};

/**
 * The debugger toolbar.
 *
 * Illegal verbs are rendered `disabled`, never hidden and never allowed to no-op: a control that
 * silently does nothing is the failure mode this whole program exists to remove, and a control that
 * disappears teaches the operator nothing about *why* it is unavailable.
 */
export function DebugToolbar({
    state,
    onVerb,
    runToTarget,
    compact,
}: {
    state: RunDebugState;
    onVerb: (verb: DebugVerb, arg?: string) => void;
    /** The node `run-to` would target (the pinned/selected one). */
    runToTarget?: string | null;
    compact?: boolean;
}) {
    const legal = new Set(state.legal);
    return (
        <div
            className={`fn-run-debug-bar${compact ? " fn-run-debug-bar-compact" : ""}`}
            role="toolbar"
            aria-label="Flow debugger controls"
        >
            <span className={`fn-run-debug-status fn-run-debug-status-${state.status}`}>
                {STATUS_TEXT[state.status]}
                {state.status === "paused" && state.atNodeId ? (
                    <span className="fn-run-debug-at"> at {state.atNodeId}</span>
                ) : null}
            </span>
            {state.reason ? (
                <span className="fn-run-debug-reason" title={state.reason}>
                    {state.reason}
                </span>
            ) : null}
            <span className="fn-run-header-spacer" />
            {VERB_SPEC.map(({ verb, glyph, label, hint }) => {
                // `run-to` additionally needs a target; without one it would be a no-op.
                const enabled = legal.has(verb) && (verb !== "run-to" || !!runToTarget);
                const title =
                    verb === "run-to" && legal.has(verb) && !runToTarget
                        ? "Select a node on the canvas first, then run to it"
                        : hint;
                return (
                    <button
                        key={verb}
                        type="button"
                        className={`fn-run-debug-btn fn-run-debug-btn-${verb}`}
                        disabled={!enabled}
                        aria-disabled={!enabled}
                        title={title}
                        aria-label={label}
                        onClick={() => onVerb(verb, verb === "run-to" ? runToTarget ?? undefined : undefined)}
                    >
                        <span aria-hidden="true">{glyph}</span>
                        <span className="fn-run-debug-btn-label">{label}</span>
                    </button>
                );
            })}
        </div>
    );
}

/** `turnBudget` is an object in the canonical definition and a JSON string in server rows. */
export function parseTurnBudget(
    raw: TurnBudgetDefinition | string | null | undefined
): TurnBudgetDefinition | undefined {
    if (!raw) return undefined;
    if (typeof raw === "string") {
        try {
            const parsed = JSON.parse(raw) as TurnBudgetDefinition;
            return parsed && typeof parsed === "object" ? parsed : undefined;
        } catch {
            return undefined;
        }
    }
    return raw;
}

/** One budget-aware pill. `exceeded` reads as exceeded — that is the whole point of showing it. */
export interface BudgetPill {
    key: string;
    label: string;
    value: string;
    /** Over the hard budget (or over soft, flagged `warn`). */
    tone?: "warn" | "exceeded";
    title?: string;
    /**
     * Artifact paths this pill refers to (flow-platform F15). Present ⇒ the pill is a BUTTON that
     * opens them. The pills have named these files since the data plane shipped and there was no
     * way to reach one: you could read "3 artifacts · 6.0MB" and its paths in a tooltip, then had
     * to go and find them by hand.
     */
    openPaths?: string[];
}

/**
 * Per-node pills: tokens, turns-vs-budget, duration, model, tool calls.
 *
 * Sources are the honest ones, and they are NOT all `ActionRecord`: tokens and turns live on
 * `NodeRunMeta` (fed from the provider's usage event), tool counts on `meta.tools`, and duration on
 * `meta.durationMs`. A missing number yields NO pill — never a derived one. `turnsUsed` in
 * particular is only present when the provider reported `num_turns`; estimating it from action
 * counts would render a budget reading nobody measured.
 */
export function budgetPills(
    meta: NodeRunMeta | undefined,
    node: NodeDefinition | undefined,
    opts?: { show?: ReadonlyArray<string> }
): BudgetPill[] {
    const show = opts?.show;
    const want = (key: string) => !show || show.includes(key);
    const pills: BudgetPill[] = [];
    if (!meta) return pills;

    if (want("tokens") && meta.totalTokens != null) {
        const io =
            meta.inputTokens != null && meta.outputTokens != null
                ? ` (${meta.inputTokens.toLocaleString()} in / ${meta.outputTokens.toLocaleString()} out)`
                : "";
        pills.push({
            key: "tokens",
            label: "tokens",
            value: meta.totalTokens.toLocaleString(),
            title: `${meta.totalTokens.toLocaleString()} tokens${io}`,
        });
    }

    // Artifact pill (flow-data-plane W5): what this node put on disk instead of into the next
    // prompt. Rows appear only when the store could MEASURE them (JSON array / JSONL / CSV) —
    // "3 artifacts · 6.0MB" with no row count is honest; an invented count is not.
    if (want("artifacts") && meta.artifacts?.length) {
        const bytes = meta.artifacts.reduce((n, a) => n + (a.bytes ?? 0), 0);
        const rows = meta.artifacts.reduce(
            (n, a) => (a.rows != null ? (n ?? 0) + a.rows : n),
            undefined as number | undefined
        );
        const n = meta.artifacts.length;
        pills.push({
            key: "artifacts",
            label: n === 1 ? "artifact" : "artifacts",
            value: rows != null ? `${rows.toLocaleString()} rows · ${fmtBytes(bytes)}` : fmtBytes(bytes),
            title:
                meta.artifacts
                    .map((a) => `${a.path} — ${fmtBytes(a.bytes)}${a.rows != null ? `, ${a.rows} rows` : ""} (${a.mediaType})`)
                    .join("\n") +
                "\n\nThe payload stays on the executor's machine; only the handle travels." +
                "\nClick to open.",
            openPaths: meta.artifacts.map((a) => a.path).filter(Boolean),
        });
    }

    // A node that was handed a handle and never opened the file is reasoning about a path string
    // (Q3). A WARNING, never a block — but never invisible either.
    if (want("artifacts") && meta.handleUnread?.length) {
        pills.push({
            key: "handle-unread",
            label: "handle unread",
            value: String(meta.handleUnread.length),
            tone: "warn",
            title:
                "This node was given an artifact reference and never read the file:\n" +
                meta.handleUnread.join("\n") +
                "\n\nIts answer may be about the path string rather than the data.",
        });
    }

    // flow-platform F2: compare against what ENFORCEMENT counted, not the CLI's `num_turns`.
    // A live node rendered "15/11 exceeded" while the ceiling never fired, because the pill and the
    // ceiling were reading two different numbers. `budgetTurns` is what the ceiling measured.
    const usedTurns = meta.budgetTurns ?? meta.turnsUsed;
    if (want("turns") && usedTurns != null) {
        const soft = meta.turnBudgetSoft ?? parseTurnBudget(node?.turnBudget)?.soft;
        const hard = meta.turnBudgetHard ?? parseTurnBudget(node?.turnBudget)?.hard;
        const cap = hard ?? soft;
        const tone: BudgetPill["tone"] =
            hard != null && usedTurns > hard
                ? "exceeded"
                : soft != null && usedTurns > soft
                    ? "warn"
                    : undefined;
        pills.push({
            key: "turns",
            label: "turns",
            value: cap != null ? `${usedTurns}/${cap}` : String(usedTurns),
            tone,
            title:
                cap != null
                    ? `${usedTurns} turns used of a ${hard != null ? "hard" : "soft"} budget of ${cap}` +
                      (meta.budgetTurns != null && meta.turnsUsed != null && meta.turnsUsed !== meta.budgetTurns
                          ? ` (the CLI reports ${meta.turnsUsed} by its own count; the budget acts on ${meta.budgetTurns})`
                          : "")
                    : `${usedTurns} turns used (no budget declared)`,
        });
    }

    if (want("duration") && meta.durationMs != null) {
        pills.push({
            key: "duration",
            label: "duration",
            value: fmtMs(meta.durationMs),
            title: `${meta.durationMs} ms`,
        });
    }

    if (want("model") && meta.model) {
        pills.push({ key: "model", label: "model", value: meta.model });
    }

    if (want("tools")) {
        const calls = Object.values(meta.tools ?? {}).reduce((a, b) => a + b, 0);
        if (calls > 0) {
            pills.push({ key: "tools", label: "tool calls", value: String(calls) });
        }
    }

    return pills;
}

function fmtMs(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const s = ms / 1000;
    if (s < 90) return `${s.toFixed(1)}s`;
    return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
}

/**
 * Renders {@link budgetPills} output.
 *
 * `onOpenArtifact` (flow-platform F15) makes a pill that names files clickable. Optional so a
 * surface with no way to open a local file — the KnowIt Run Center, where the artifact is on
 * someone else's machine — renders exactly what it did before, rather than offering a button that
 * cannot work.
 */
export function BudgetPills(
    { pills, onOpenArtifact }: { pills: BudgetPill[]; onOpenArtifact?: (path: string) => void },
) {
    if (pills.length === 0) return null;
    return (
        <div className="fn-run-pill-group">
            <div className="fn-run-pill-label">Budget</div>
            <div className="fn-run-pills">
                {pills.map((p) => {
                    // flow-platform F15: a pill naming files becomes a real button — keyboard
                    // reachable and labelled, not a span with a click handler bolted on.
                    const clickable = !!(p.openPaths?.length && onOpenArtifact);
                    const className =
                        `fn-run-pill fn-run-pill-budget${p.tone ? ` fn-run-pill-${p.tone}` : ""}` +
                        (clickable ? " fn-run-pill-clickable" : "");
                    const body = (
                        <>
                            <span className="fn-run-pill-k">{p.label}</span>
                            <span className="fn-run-pill-v">{p.value}</span>
                            {p.tone === "exceeded" ? (
                                <span className="fn-run-pill-flag" aria-label="budget exceeded">!</span>
                            ) : null}
                        </>
                    );
                    return clickable ? (
                        <button
                            key={p.key}
                            type="button"
                            className={className}
                            title={p.title ?? `${p.label}: ${p.value}`}
                            aria-label={`Open ${p.openPaths!.length} artifact file(s)`}
                            onClick={() => p.openPaths!.forEach((path) => onOpenArtifact!(path))}
                        >
                            {body}
                        </button>
                    ) : (
                        <span
                            key={p.key}
                            className={className}
                            title={p.title ?? `${p.label}: ${p.value}`}
                        >
                            {body}
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

/** The prompt the executor interpolated for a node, from its transcript. */
function promptOf(trace: NodeTraceEntry[] | undefined): string | undefined {
    return trace?.find((e) => e.kind === "prompt")?.text;
}

/**
 * The predecessor result the executor substituted — what this node is actually working from.
 *
 * **This is a PREVIEW, and it says so when it is one.** The executor carries the predecessor's FULL
 * result in its context (`context[nodeId]`, uncapped) and interpolates all of it into this node's
 * prompt. What the panel can see is a different, capped copy: transcript entries clamp at
 * {@link MAX_TRACE_ENTRY_CHARS} and `ActionRecord.detail` at {@link MAX_ACTION_DETAIL_CHARS}. So a
 * long result reaches the node in full but reaches this card in part — and a card that showed 1 KB
 * of a 7 KB input without saying so would be telling the operator the node got less than it did.
 * The same honesty rule the pills follow: show what was measured, and mark what was clipped.
 */
function resolvedInputOf(
    flow: FlowDefinition,
    nodeId: string,
    metas: Record<string, NodeRunMeta>,
    traces: Record<string, NodeTraceEntry[]>,
    actions: Record<string, ActionRecord[]>
): { fromNodeId: string; text: string; clippedAt?: number } | undefined {
    const preds = (flow.nodes ?? []).filter((n) =>
        (n.transitions ?? []).some((t) => t.nextNodeId === nodeId)
    );
    // The predecessor that most recently ran is the one whose result was carried forward.
    let best: { fromNodeId: string; at: number } | undefined;
    for (const p of preds) {
        const at = metas[p.nodeId]?.startedAt ?? -1;
        if (at >= 0 && (!best || at > best.at)) best = { fromNodeId: p.nodeId, at };
    }
    const fromNodeId = best?.fromNodeId ?? preds[0]?.nodeId;
    if (!fromNodeId) return undefined;

    const resultTrace = [...(traces[fromNodeId] ?? [])]
        .reverse()
        .find((e) => e.kind === "result" || e.kind === "content");
    if (resultTrace?.text) {
        return {
            fromNodeId,
            text: resultTrace.text,
            // At the clamp ⇒ the transcript copy was cut here. The node still received all of it.
            clippedAt: resultTrace.text.length >= MAX_TRACE_ENTRY_CHARS ? MAX_TRACE_ENTRY_CHARS : undefined,
        };
    }

    const resultAction = [...(actions[fromNodeId] ?? [])]
        .reverse()
        .find((r) => r.kind === "result");
    if (resultAction) {
        const text = resultAction.detail ?? resultAction.summary;
        return {
            fromNodeId,
            text,
            clippedAt: text.length >= MAX_ACTION_DETAIL_CHARS ? MAX_ACTION_DETAIL_CHARS : undefined,
        };
    }

    return undefined;
}

/**
 * The payload of the node the run is held at.
 *
 * This is the job the approval modal does today, moved into the panel you are already watching:
 * what the node will be told, what it is working from, what it will cost, and where each outcome
 * would send the run. Without it, "step" is a button you press hoping for the best.
 */
export function PausedNodeCard({
    flow,
    nodeId,
    state,
    metas,
    traces,
    actions,
    approval,
    onDecision,
    onVerb,
}: {
    flow: FlowDefinition;
    nodeId: string;
    state: RunDebugState;
    metas: Record<string, NodeRunMeta>;
    traces: Record<string, NodeTraceEntry[]>;
    actions: Record<string, ActionRecord[]>;
    approval?: RunApprovalRequest | null;
    onDecision?: (nodeId: string, approved: boolean) => void;
    onVerb?: (verb: DebugVerb, arg?: string) => void;
}) {
    const node = (flow.nodes ?? []).find((n) => n.nodeId === nodeId);
    const meta = metas[nodeId];
    const budget = parseTurnBudget(node?.turnBudget);
    // The approval's prompt is authoritative when one is pending — the node has not run yet, so
    // there is no transcript to read it from.
    const prompt = approval?.prompt ?? promptOf(traces[nodeId]);
    const input = resolvedInputOf(flow, nodeId, metas, traces, actions);
    const transitions = node?.transitions ?? [];
    const awaiting = state.status === "awaiting-approval" && !!approval;

    return (
        <div className={`fn-panel fn-run-paused-card${awaiting ? " fn-run-paused-approval" : ""}`}>
            <div className="fn-panel-title">
                {awaiting ? "Approval required" : "Held before"}: {node?.name || nodeId}
            </div>
            <div className="fn-run-inspector-sub">
                {nodeId}
                {state.reason ? ` · ${state.reason}` : ""}
            </div>

            <div className="fn-run-facts">
                {node?.ownerRole ? <CardFact k="Owner" v={node.ownerRole} /> : null}
                {approval?.model || node?.model || meta?.model ? (
                    <CardFact k="Model" v={String(approval?.model ?? node?.model ?? meta?.model)} />
                ) : null}
                {node?.effort ? <CardFact k="Effort" v={String(node.effort)} /> : null}
                {budget?.soft != null || budget?.hard != null ? (
                    <CardFact
                        k="Turn budget"
                        v={`${budget?.soft ?? "—"} soft / ${budget?.hard ?? "—"} hard`}
                    />
                ) : null}
                {node?.requireApproval ? <CardFact k="Approval" v="required by the flow" /> : null}
            </div>

            {input ? (
                <details className="fn-run-paused-section">
                    <summary>
                        Resolved input <span className="fn-run-paused-from">from {input.fromNodeId}</span>
                        {input.clippedAt ? (
                            <span className="fn-run-paused-clip">
                                {" "}· preview, first {input.clippedAt.toLocaleString()} chars
                            </span>
                        ) : null}
                    </summary>
                    {input.clippedAt ? (
                        <div className="fn-run-paused-clip-note">
                            The node received the predecessor&apos;s result in full — this panel copy is
                            clipped at {input.clippedAt.toLocaleString()} characters.
                        </div>
                    ) : null}
                    <pre className="fn-run-paused-pre">{input.text}</pre>
                </details>
            ) : null}

            {prompt ? (
                <details className="fn-run-paused-section" open={awaiting}>
                    <summary>Prompt</summary>
                    <pre className="fn-run-paused-pre">{prompt}</pre>
                </details>
            ) : null}

            {transitions.length > 0 ? (
                <div className="fn-run-paused-section fn-run-paused-transitions">
                    <div className="fn-run-pill-label">Transitions that would fire</div>
                    <ul className="fn-run-paused-transitions-list">
                        {transitions.map((t, i) => (
                            <li key={`${t.condition}-${t.nextNodeId}-${i}`}>
                                <code>{t.condition || "always"}</code>
                                <span aria-hidden="true"> → </span>
                                <span className="fn-run-paused-target">
                                    {t.nextNodeId || "end of flow"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {awaiting && approval ? (
                <div className="fn-run-paused-actions">
                    <button
                        type="button"
                        className="fn-run-debug-btn fn-run-approve"
                        onClick={() => onDecision?.(approval.nodeId, true)}
                    >
                        Approve
                    </button>
                    <button
                        type="button"
                        className="fn-run-debug-btn fn-run-deny"
                        onClick={() => onDecision?.(approval.nodeId, false)}
                        title={
                            approval.denialTargetLabel
                                ? `Deny → ${approval.denialTargetLabel}`
                                : "Deny → the node fails with ERROR: approval-denied"
                        }
                    >
                        Deny
                    </button>
                </div>
            ) : state.status === "paused" ? (
                <div className="fn-run-paused-actions">
                    <button
                        type="button"
                        className="fn-run-debug-btn"
                        disabled={!state.legal.includes("step")}
                        onClick={() => onVerb?.("step")}
                    >
                        Step this node
                    </button>
                    <button
                        type="button"
                        className="fn-run-debug-btn"
                        disabled={!state.legal.includes("continue")}
                        onClick={() => onVerb?.("continue")}
                    >
                        Continue
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function CardFact({ k, v }: { k: string; v: string }) {
    return (
        <div className="fn-run-fact">
            <span className="fn-run-fact-k">{k}</span>
            <span className="fn-run-fact-v">{v}</span>
        </div>
    );
}
